import type { ProBuilderSessionView } from '~/hooks/queries/use-pro-builder-session';
import { uploadNoticeText } from './pro-builder-copy';
import type { UploadRow } from './pro-builder-upload-store';

/**
 * After files are uploaded (dropped on the terminal or picked in the dialog), Eddy tells Claude Code by typing
 * one line naming them and pressing Enter. These are the decisions behind that, kept pure so they can be tested
 * without a terminal.
 */

/**
 * Whether typed text reaches Claude Code's prompt box as a message. Only while Claude Code runs and is not
 * asking something: on a permission question Enter would pick the highlighted answer, and once Claude Code
 * has exited the line would run as a shell command. An unknown state (hooks not fired yet) waits too.
 */
export function claudeTakesMessages(session: Pick<ProBuilderSessionView, 'state' | 'live'> | null): boolean {
  if (!session || !session.live) return false;
  return session.state === 'idle' || session.state === 'finished' || session.state === 'failed' || session.state === 'working';
}

/**
 * Whether the terminal may type a notice right now: the terminal is open, Claude Code takes messages, AND
 * `eddy.ui-refactoring` is on (FRONT-3223) — flag off, uploads are never announced, as before FRONT-3138.
 */
export function uploadNoticeReady(input: { composerFlag: boolean; terminalOpen: boolean; session: Pick<ProBuilderSessionView, 'state' | 'live'> | null }): boolean {
  return input.composerFlag && input.terminalOpen && claudeTakesMessages(input.session);
}

/**
 * The notice owed for `folder` (the app the terminal runs in): every saved, not-yet-announced file, but only
 * once that app has nothing queued or uploading, so one drop of several files is one message. Rows for
 * another app stay unannounced until that app is open again.
 */
export function pendingUploadNotice(rows: readonly UploadRow[], folder: string | null): { ids: number[]; text: string } | null {
  if (!folder) return null;
  const mine = rows.filter((r) => r.folder === folder);
  if (mine.some((r) => r.state === 'queued' || r.state === 'uploading')) return null;
  const owed = mine.filter((r) => r.state === 'done' && !r.announced && r.savedName);
  if (owed.length === 0) return null;
  return { ids: owed.map((r) => r.id), text: uploadNoticeText(owed.map((r) => typeableName(r.savedName!))) };
}

/**
 * A file name as safe keystrokes: control characters (Enter, Escape, and the escape sequences built on them)
 * would act on the terminal rather than be typed, so each becomes a space.
 */
export function typeableName(name: string): string {
  // eslint-disable-next-line no-control-regex
  return name.replace(/[\u0000-\u001f\u007f-\u009f]/g, ' ');
}

/**
 * How long after the text Enter is sent. Sent in the same write, Claude Code reads the burst as a paste and
 * puts the Enter into the message as a new line instead of sending it.
 */
export const UPLOAD_NOTICE_ENTER_DELAY_MS = 250;
